import {
  Button,
  InputProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { LimeInput } from "./LimeInput";
import { MarkdownEditor } from "./LimeMarkdownEditor";
import { Languages } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { useForm } from "@mantine/form";
import { SUPPORTED_LOCALES } from "../../../../shared/constants/locales";
import { LimeLocalePicker } from "./LimeLocalePicker";
import { useEffect, useState } from "react";
import { MantineFormValues } from "./shared.types";

export type LocalizedInputProps = {
  type: "text" | "markdown";
  values: {
    language: (typeof SUPPORTED_LOCALES)[number];
    value: string;
  }[];
  label?: string;
  handleSubmit: (values: Form) => void;
  isLoading?: boolean;
  error?: MantineFormValues["error"];
  wrapperClassName?: string;
} & Omit<InputProps, "label" | "onChange" | "onValueChange">;

type Form = {
  localizedValues: LocalizedInputProps["values"];
};

export const LocalizedInput = ({ ...props }: LocalizedInputProps) => {
  const { label, ...rest } = props;

  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const locale = i18n.locale as (typeof SUPPORTED_LOCALES)[number];
  const selectedLanguageIndex = props.values.findIndex(
    (language) => language.language === locale,
  );
  const selectedLanguageValue = props.values[selectedLanguageIndex];

  return (
    <>
      <div className={props.wrapperClassName}>
        <div className="mb-1 flex flex-row items-center justify-between">
          <p className="text-sm font-medium">
            {props.label} ({selectedLanguageValue.language.toUpperCase()})
          </p>
          <Button
            variant="flat"
            size="sm"
            color="primary"
            startContent={<Languages size={16} />}
            onPress={onOpen}
            isLoading={props.isLoading}
            isDisabled={props.isDisabled}
          >
            <Trans>Nastavitve prevodov</Trans>
          </Button>
        </div>

        <Input
          {...rest}
          value={selectedLanguageValue.value}
          onChange={(value) => {
            props.handleSubmit({
              localizedValues: props.values.map((language) =>
                language.language === selectedLanguageValue.language
                  ? { ...language, value }
                  : language,
              ),
            });
          }}
        />
      </div>

      <LanguageModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        values={props.values.map((value) => ({
          language: value.language,
          value: value.value,
        }))}
        handleSubmit={props.handleSubmit}
        type={rest.type}
      />
    </>
  );
};

const LanguageModal = ({
  isOpen,
  onOpenChange,
  handleSubmit,
  values,
  type,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  handleSubmit: (values: Form) => void;
  values: LocalizedInputProps["values"];
  type: LocalizedInputProps["type"];
}) => {
  const form = useForm<Form>({
    initialValues: {
      localizedValues: values,
    },
  });

  const [selectedLanguage, setSelectedLanguage] = useState<
    (typeof SUPPORTED_LOCALES)[number]
  >(i18n.locale as (typeof SUPPORTED_LOCALES)[number]);

  useEffect(() => {
    form.setFieldValue("localizedValues", values);
  }, [values]);

  // reset selected language when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedLanguage(i18n.locale as (typeof SUPPORTED_LOCALES)[number]);
    }
  }, [isOpen]);

  const selectedLanguageIndex = form.values.localizedValues.findIndex(
    (language) => language.language === selectedLanguage,
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalContent>
        {(onClose) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              form.validate();

              if (form.isValid()) {
                handleSubmit(form.getValues());
                onClose();
              }
            }}
          >
            <ModalHeader>
              <Trans>Nastavitve prevodov</Trans>
            </ModalHeader>
            <ModalBody>
              <LimeLocalePicker
                value={selectedLanguage}
                onChange={setSelectedLanguage}
              />

              <Input
                {...form.getInputProps(
                  `localizedValues.${selectedLanguageIndex}.value`,
                )}
                type={type}
              />
            </ModalBody>
            <ModalFooter>
              <Button onPress={onClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button color="primary" type="submit">
                <Trans>Shrani</Trans>
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

const Input = ({
  onChange,
  value,
  type,
  isLoading,
  error,
  isDisabled,
}: {
  onChange: (value: string) => void;
  value?: string;
  type: LocalizedInputProps["type"];
  isLoading?: boolean;
  error?: MantineFormValues["error"];
  isDisabled?: boolean;
}) => {
  if (type === "markdown") {
    return (
      <MarkdownEditor
        value={value}
        onChange={onChange}
        excludeActions={["link", "image"]}
        isDisabled={isDisabled}
      />
    );
  }

  return (
    <LimeInput
      error={error}
      onValueChange={onChange}
      value={value}
      isDisabled={isLoading || isDisabled}
    />
  );
};
